import { Box, Stack, Typography, useTheme } from "@mui/material";
import React, { useState } from "react";
import { StyledTextField } from "../../layer01";
import { LargeCustomIcon, MediumCustomIcon } from "../../layer00";

type MessageInputProps = {
  // Return false to reject the message (e.g. Bo is still replying) so the
  // typed text is kept instead of cleared.
  onSend: (message: string) => boolean | void;
  disabled?: boolean;
  note?: {
    variant: "info" | "warning";
    text: React.ReactNode;
  };
};

export const MessageInput = (props: MessageInputProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const noteIcon =
    props.note?.variant === "warning" ? "WARNING" : "INFORMATION";
  const noteColorDefault = isDark ? "var(--sapphire-50)" : theme.palette.info.main;
  const noteColor =
    props.note?.variant === "warning" ? theme.palette.warning.main : noteColorDefault;

  const [message, setMessage] = useState("");

  const handleSend = () => {
    if (props.disabled) return;
    const trimmed = message.trim();
    if (trimmed.length === 0) {
      return;
    }
    if (props.onSend(trimmed) === false) {
      return;
    }
    setMessage("");
  };

  // Enter sends, Shift+Enter adds a new line, matching the main chat input.
  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Enter confirms an IME composition (e.g. Japanese); Safari reports keyCode 229 with isComposing already false.
    if (
      event.key !== "Enter" ||
      event.shiftKey ||
      event.nativeEvent.isComposing ||
      event.keyCode === 229
    ) {
      return;
    }
    event.preventDefault();
    handleSend();
  };

  return (
    <Box
      marginTop="1rem"
      sx={{
        position: "sticky",
        marginRight: {
          xs: "3.5rem",
          sm: "1rem",
        },
        bottom: {
          xs: 0,
          sm: "2rem",
        },
      }}
    >
      <Box
        className={isDark ? "bg-teamApp-dark-surface" : "bg-white"}
        borderRadius="1rem"
        sx={{
          border: isDark
            ? "1px solid var(--team-app-text-tertiary)"
            : "1px solid var(--sapphire-30)",
          padding: {
            xs: ".625rem",
            sm: ".5rem",
          },
          boxShadow: isDark
            ? "0px 8px 16px var(--team-app-input-shadow-dark)"
            : "0px 8px 16px var(--team-app-input-shadow-light)",
        }}
      >
        <Box display="flex" alignItems="center" gap=".5rem">
          <Box flex="1 1 0" minWidth={0}>
            <StyledTextField
              multiline
              value={message}
              disabled={props.disabled}
              sx={{
                margin: 0,
                border: "none",
                borderRadius: ".75rem",
                background: isDark
                  ? "var(--team-app-dark-stripe-base)"
                  : "var(--team-app-stripe-mid)",
                paddingLeft: ".75rem",
                paddingRight: ".75rem",
                lineHeight: "1.25rem",
                paddingTop: {
                  xs: ".875rem",
                  sm: ".625rem",
                },
                paddingBottom: {
                  xs: ".875rem",
                  sm: ".625rem",
                },
              }}
              placeholder="Chat with Bo about anything..."
              onKeyDown={handleKeyDown}
              onChange={(e) => setMessage(e.currentTarget.value)}
              fullWidth
            />
          </Box>
          <Box
            onClick={handleSend}
            display="flex"
            bgcolor={(t) => t.palette.primary.main}
            justifyContent="center"
            alignItems="center"
            borderRadius=".75rem"
            flexShrink={0}
            height={{
              xs: "3rem",
              sm: "2.5rem",
            }}
            width={{
              xs: "3rem",
              sm: "2.5rem",
            }}
            sx={{
              cursor: props.disabled ? "not-allowed" : "pointer",
              opacity: props.disabled ? 0.4 : 1,
            }}
          >
            <LargeCustomIcon icon="SEND" stroke="white" />
          </Box>
        </Box>
        {props.note && (
          <Stack
            direction="row"
            alignItems="center"
            columnGap=".375rem"
            sx={{
              paddingTop: ".625rem",
              paddingLeft: ".25rem",
            }}
          >
            <MediumCustomIcon icon={noteIcon} stroke={noteColor} />
            <Typography
              sx={{
                color: noteColor,
                fontSize: ".8125rem",
                fontWeight: 500,
              }}
            >
              {props.note.text}
            </Typography>
          </Stack>
        )}
      </Box>
    </Box>
  );
};
